import {
  MediaCardMetadata,
  MediaCardType,
} from '@wandb/weave/common/types/media';
import {includes, omit} from 'lodash';
import React, {useMemo} from 'react';
import {useParams} from 'react-router-dom';

import * as PanelTypes from '../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../state/views/types';
import {ClassLabelMap, SliderKeyboardOperation} from '../../util/media';
import {keyDisplayName} from '../../util/runs';
import {RunWithHistoryAndMediaWandb} from '../MediaCard';
import {useDetectPanelVariant} from '../Panel/hooks/hooks';
import {Axis, KeyTypes} from './common';
import {
  Layout,
  MediaBrowserPanelProps,
  MediaConfigWithDefaultsAndMediaKeys,
  SizingSettings,
} from './Component';
import {MediaBrowserModalContainer} from './modal/MediaBrowserModalContainer';
import {Pagination} from './Pagination';
import {PanelBody} from './PanelBody';
import {PanelContent} from './PanelContent';
import {PanelFooter} from './PanelFooter';
import {PanelHeader} from './PanelHeader';
import {TilePagination} from './tiles';
import {StepMeta} from './types';
import {getTitleFromConfig} from './utils/getTitleFromConfig';

type MediaBrowserPanelInnerProps = Omit<MediaBrowserPanelProps, ''> & {
  config: MediaConfigWithDefaultsAndMediaKeys;
  layout: Layout;
  mediaType: MediaCardType;
  firstMediaMetadata: MediaCardMetadata | null;
  currentStep: number;
  currentSliderValue: number;
  mediaBrowserWidth: number;
  mediaBrowserHeight: number;
  stepMeta: StepMeta;
  panelViewerRef: React.MutableRefObject<HTMLDivElement | null>;
  allMasks: ClassLabelMap;
  allBoxes: ClassLabelMap;
  sizingSettings: SizingSettings;
  allMediaKeys: string[];
  keyTypes: KeyTypes;
  numericKeys: string[] | undefined;
  tilePagination: TilePagination | null;
  mediaBrowserRefCallback: (el: HTMLDivElement) => void;
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  isMaxStepLoading: boolean;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
};

const MediaBrowserPanelInnerComp = (props: MediaBrowserPanelInnerProps) => {
  const {
    config,
    configMode,
    disableRunLinks,
    layout,
    mediaType,
    firstMediaMetadata,
    currentStep,
    currentSliderValue,
    mediaBrowserWidth,
    mediaBrowserHeight,
    stepMeta,
    panelViewerRef,
    allMasks,
    allBoxes,
    sizingSettings,
    allMediaKeys,
    keyTypes,
    numericKeys,
    tilePagination,
    updateConfig,
    mediaBrowserRefCallback,
    customRunNames,
    isMaxStepLoading,
    runsWithMedia,
  } = props;
  const {mediaKeys, mediaIndex, gallerySettings, gridSettings, mode} = config;

  // Only want to include keyboard bindings when panel viewed in full screen.
  const {isFullScreenPanelPage} = useDetectPanelVariant();

  const stepSliderKeyboardBindings = isFullScreenPanelPage
    ? {
        'Meta+ArrowRight': SliderKeyboardOperation.INCREMENT,
        'Meta+ArrowLeft': SliderKeyboardOperation.DECREMENT,
        'Control+ArrowRight': SliderKeyboardOperation.INCREMENT,
        'Control+ArrowLeft': SliderKeyboardOperation.DECREMENT,
      }
    : undefined;

  // Use index data from stepMeta instead of recalculating
  const minMediaIndex = stepMeta.minIndex;
  const maxMedia = stepMeta.maxIndex;

  const galleryAxes = useMemo(
    () => [gallerySettings.axis],
    [gallerySettings.axis]
  );

  const gridAxes = useMemo(() => {
    return [gridSettings.xAxis, gridSettings.yAxis];
  }, [gridSettings.xAxis, gridSettings.yAxis]);

  const activeAxes = mode === 'gallery' ? galleryAxes : gridAxes;

  const {entityName} = useParams<{entityName: string}>();

  const labels = useMemo(() => {
    // In gallery mode we want to label everything by what we are iterating across
    let labelList: Axis[] = [];
    if (mode === 'gallery' && galleryAxes[0] !== 'index') {
      labelList = labelList.concat(galleryAxes);
    } else if (gridAxes[0] === 'run') {
      labelList.push('run');
    }
    return labelList;
  }, [mode, galleryAxes, gridAxes]);

  // what are you doing
  const stepSlider = {
    current: currentSliderValue,
    min: stepMeta?.minSliderStep ?? stepMeta.min,
    max: stepMeta?.maxSliderStep ?? stepMeta.max,
    steps: stepMeta.sliderSteps ?? stepMeta.steps,
    key: stepMeta.sliderKey,
    label: keyDisplayName({section: 'summary', name: stepMeta.sliderKey}),
    active: !includes(activeAxes, 'step'),
    keyboardBindings: stepSliderKeyboardBindings,
    strideLength: config.stepStrideLength ?? 1,
  };

  const indexSlider = {
    current: mediaIndex ?? 0,
    min: minMediaIndex,
    max: maxMedia,
    active: !includes(activeAxes, 'index'),
  };

  const mediaBrowserSize = useMemo(() => {
    return {
      width: mediaBrowserWidth,
      height: mediaBrowserHeight - (tilePagination != null ? 30 : 0),
    };
  }, [tilePagination, mediaBrowserWidth, mediaBrowserHeight]);

  const headerSubConfig = {
    ...config,
    chartTitle: getTitleFromConfig(config),
  };

  // Memoize the props objects to prevent unnecessary re-renders
  const panelContentProps = useMemo(
    () => ({
      labels,
      mediaType,
      mediaBrowserSize,
      sizingSettings,
      layout,
      currentMediaKeys: mediaKeys,
      config: omit(config, 'chartTitle'),
      updateConfig,
      panelRef: props.panelRef,
      disableRunLinks,
      allMasks,
      currentStep,
      tilePagination,
      customRunNames,
      isMaxStepLoading,
    }),
    [
      labels,
      mediaType,
      mediaBrowserSize,
      sizingSettings,
      layout,
      mediaKeys,
      config,
      updateConfig,
      props.panelRef,
      disableRunLinks,
      allMasks,
      currentStep,
      tilePagination,
      customRunNames,
      isMaxStepLoading,
    ]
  );

  return (
    <MediaBrowserModalContainer
      runsWithMedia={runsWithMedia}
      stepMeta={stepMeta}
      sliderConfig={{
        snapToExistingStep: config.snapToExistingStep ?? true,
        stepStrideLength: stepSlider.strideLength,
      }}
      onModalClose={(finalStep, finalIndex) => {
        updateConfig({
          stepIndex: finalStep,
          mediaIndex: finalIndex,
        });
      }}>
      <PanelBody
        configMode={configMode}
        panelViewerRef={panelViewerRef}
        mediaBrowserRefCallback={mediaBrowserRefCallback}
        mediaType={mediaType}
        sizingSettings={sizingSettings}
        configWithDefaults={config}
        allMediaKeys={allMediaKeys}
        keyTypes={keyTypes}
        numericKeys={numericKeys ?? []}
        updateConfig={updateConfig}
        header={<PanelHeader config={headerSubConfig} />}
        footer={
          <PanelFooter
            {...{
              config: omit(config, 'chartTitle'),
              updateConfig,
              sliders: {step: stepSlider, index: indexSlider},
              layout,
              isMaxStepLoading,
              mediaBrowserSize,
            }}
          />
        }>
        {tilePagination != null && (
          <Pagination
            panelViewerRef={panelViewerRef}
            updateConfig={updateConfig}
            tilePagination={tilePagination}
          />
        )}
        <PanelContent {...panelContentProps} />
      </PanelBody>
    </MediaBrowserModalContainer>
  );
};

export const MediaBrowserPanelInner = React.memo(MediaBrowserPanelInnerComp);
